import { Button } from 'components/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/shadcn/dialog';
import { Input } from 'components/shadcn/input';
import { Label } from 'components/shadcn/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/shadcn/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'components/shadcn/table';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ModelMetadata, ModelVendor } from 'types';
import { apiUrl, fetchWithAuth } from 'utils';

const defaultVendor: ModelVendor = 'OPENAI' as const;

export const ModelMetadataList = () => {
  const { t } = useTranslation();
  const [models, setModels] = useState<ModelMetadata[]>([]);
  const [selectedModel, setSelectedModel] = useState<ModelMetadata | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Form state
  const [dialogName, setDialogName] = useState('');
  const [dialogDisplayName, setDialogDisplayName] = useState('');
  const [dialogInputExchangeRate, setDialogInputExchangeRate] = useState('0');
  const [dialogOutputExchangeRate, setDialogOutputExchangeRate] = useState('0');
  const getVendorName = (v: ModelVendor) =>
    v === 'GOOGLE' ? t('dashboard.models.vendors.google') : t('dashboard.models.vendors.openai');

  const [dialogVendor, setDialogVendor] = useState<ModelVendor>(defaultVendor);

  const isEditing = !!selectedModel && !isCreating;

  const fetchModels = async () => {
    try {
      const response = await fetchWithAuth(`${apiUrl}/model-metadata`);
      const data = await response.json();
      setModels(data);
    } catch (error) {
      console.error('Error fetching models:', error);
    }
  };

  useEffect(() => {
    fetchModels();
  }, []);

  useEffect(() => {
    if (selectedModel) {
      setDialogName(selectedModel.name);
      setDialogDisplayName(selectedModel.displayName);
      setDialogInputExchangeRate(selectedModel.inputExchangeRate.toString());
      setDialogOutputExchangeRate(selectedModel.outputExchangeRate.toString());
      setDialogVendor(selectedModel.vendor);
    } else {
      setDialogName('');
      setDialogDisplayName('');
      setDialogInputExchangeRate('0');
      setDialogOutputExchangeRate('0');
      setDialogVendor(defaultVendor);
    }
  }, [selectedModel]);

  const handleDelete = async (id: number) => {
    if (!window.confirm(t('dashboard.models.confirmDelete'))) {
      return;
    }

    try {
      await fetchWithAuth(`${apiUrl}/model-metadata/${id}`, {
        method: 'DELETE',
      });

      // Update the local state to reflect the deletion
      setModels(models.filter((model) => model.id !== id));
    } catch (error) {
      console.error('Error deleting model:', error);
    }
  };

  const submitDialog = async () => {
    setIsLoading(true);

    try {
      const payload = {
        name: dialogName,
        displayName: dialogDisplayName,
        inputExchangeRate: parseFloat(dialogInputExchangeRate),
        outputExchangeRate: parseFloat(dialogOutputExchangeRate),
        vendor: dialogVendor,
      };

      let response;

      if (isCreating) {
        response = await fetchWithAuth(`${apiUrl}/model-metadata`, {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      } else {
        response = await fetchWithAuth(`${apiUrl}/model-metadata/${selectedModel?.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
      }

      if (!response.ok) {
        throw new Error(t('errors.generalError'));
      }

      // Refresh the model list
      await fetchModels();

      // Close the dialog
      setSelectedModel(null);
      setIsCreating(false);
    } catch (error) {
      console.error('Error saving model:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mx-auto py-6 px-4 lg:px-6 lg:max-w-5xl grow self-start box-border w-full">
      <div className="flex flex-row gap-2 justify-between items-center mb-10">
        <h1 className="text-4xl font-black text-primary text-balance">
          {t('dashboard.models.title')}
        </h1>
        <Button color="success" onClick={() => setIsCreating(true)}>
          <Plus className="me-2 h-4 w-4" /> {t('dashboard.models.createModel')}
        </Button>
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('dashboard.models.table.id')}</TableHead>
              <TableHead>{t('dashboard.models.table.name')}</TableHead>
              <TableHead>{t('dashboard.models.table.displayName')}</TableHead>
              <TableHead>{t('dashboard.models.table.vendor')}</TableHead>
              <TableHead>{t('dashboard.models.table.inputExchangeRate')}</TableHead>
              <TableHead>{t('dashboard.models.table.outputExchangeRate')}</TableHead>
              <TableHead>{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {models.map((model) => (
              <TableRow key={model.id}>
                <TableCell>{model.id}</TableCell>
                <TableCell>{model.name}</TableCell>
                <TableCell>{model.displayName}</TableCell>
                <TableCell>{getVendorName(model.vendor)}</TableCell>
                <TableCell>{model.inputExchangeRate}</TableCell>
                <TableCell>{model.outputExchangeRate}</TableCell>
                <TableCell className="flex gap-2">
                  <Button variant="ghost" size="icon" onClick={() => setSelectedModel(model)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(model.id)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog
        open={isCreating || isEditing}
        onOpenChange={() => {
          setSelectedModel(null);
          setIsCreating(false);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {isCreating ? t('dashboard.models.createModel') : t('dashboard.models.editModel')}
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4 max-h-[500px] overflow-y-auto p-4">
            <div>
              <Label htmlFor="name">{t('dashboard.models.table.name')}</Label>
              <Input
                id="name"
                value={dialogName}
                onChange={(e) => setDialogName(e.target.value)}
                placeholder={t('dashboard.models.placeholders.name')}
              />
            </div>
            <div>
              <Label htmlFor="displayName">{t('dashboard.models.table.displayName')}</Label>
              <Input
                id="displayName"
                value={dialogDisplayName}
                onChange={(e) => setDialogDisplayName(e.target.value)}
                placeholder={t('dashboard.models.placeholders.displayName')}
              />
            </div>
            <div>
              <Label htmlFor="vendor">{t('dashboard.models.table.vendor')}</Label>
              <Select
                onValueChange={(value) => {
                  if (value === 'GOOGLE') {
                    setDialogVendor('GOOGLE');
                  } else if (value === 'OPENAI') {
                    setDialogVendor('OPENAI');
                  }
                }}
                value={dialogVendor}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('dashboard.models.selectVendor')}></SelectValue>
                </SelectTrigger>
                <SelectContent position="popper">
                  <SelectItem value="GOOGLE">{t('dashboard.models.vendors.google')}</SelectItem>
                  <SelectItem value="OPENAI">{t('dashboard.models.vendors.openai')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="inputExchangeRate">
                {t('dashboard.models.table.inputExchangeRate')}
              </Label>
              <Input
                id="inputExchangeRate"
                type="number"
                step="0.000001"
                value={dialogInputExchangeRate}
                onChange={(e) => setDialogInputExchangeRate(e.target.value)}
                placeholder={t('dashboard.models.placeholders.inputExchangeRate')}
              />
            </div>
            <div>
              <Label htmlFor="outputExchangeRate">
                {t('dashboard.models.table.outputExchangeRate')}
              </Label>
              <Input
                id="outputExchangeRate"
                type="number"
                step="0.000001"
                value={dialogOutputExchangeRate}
                onChange={(e) => setDialogOutputExchangeRate(e.target.value)}
                placeholder={t('dashboard.models.placeholders.outputExchangeRate')}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setSelectedModel(null);
                setIsCreating(false);
              }}
              disabled={isLoading}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={submitDialog}
              disabled={isLoading || !dialogName || !dialogDisplayName}
            >
              {isLoading ? t('common.saving') : isCreating ? t('common.create') : t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ModelMetadataList;
